import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminTranscriptReviewService,
  CreateTranscriptReviewSessionBodyDto,
  PreviewTranscriptReviewEditBodyDto,
  RequestTranscriptReviewEditBodyDto,
} from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { adminTranscriptReviewKeys } from 'app/queries/adminTranscriptReview/adminTranscriptReviewKeys';
import { MutableRefObject, createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createFetchStyleFixFixtureChunkQueryFn } from '../api/fetchStyleFixFixtureChunk';
import { createFetchStyleFixFixtureManifestQueryFn } from '../api/fetchStyleFixFixtureManifest';
import { createFetchTranscriptReviewSessionsQueryFn } from '../api/fetchTranscriptReviewSessions';
import {
  TRANSCRIPT_REVIEW_PROPOSAL_SOURCE,
  TRANSCRIPT_REVIEW_PROPOSAL_STATUS,
  TRANSCRIPT_REVIEW_SESSION_STATUS,
  TTranscriptReviewProposalDecision,
  s3FixtureReviewTitle,
} from '../constants';
import { getFirstEventTranscriptBefore, parseEvents, readFixtureFiles, serializeEvents } from '../helpers/events';
import { isNotFoundApiError } from '../helpers/isNotFoundApiError';
import {
  acceptLocalFixtureProposal,
  appendLocalFixtureEvents,
  appendLocalPreviewProposals,
  buildLocalFixtureProposals,
  getLoadedS3ChunkIndexesFromSession,
  getNextPendingProposalId,
  getProposalChunkIndex,
  getProposalLlmTokenCount,
  offsetStyleFixEventForTranscript,
  rejectLocalFixtureProposal,
} from '../helpers/proposals';
import {
  createLocalFixtureSession,
  getResponsePayload,
  isLocalFixtureSession,
  normalizeSession,
  upsertSession,
} from '../helpers/sessions';
import { splitTranscriptLines } from '../helpers/transcript';
import {
  TChatEditMode,
  TChatMessage,
  TFixtureSource,
  TPersistedChatSummary,
  TTranscriptChunkRange,
  TTranscriptReviewProposal,
  TTranscriptReviewSession,
} from '../types';

const JOB_ID_DEBOUNCE_MS = 300;
const FIXTURE_STALE_TIME_MS = Infinity;
const INLINE_ERROR_CALL_OPTIONS = { dontRedirectOnError: true } as const;
const EMPTY_SESSIONS: TTranscriptReviewSession[] = [];

type TChunkProposalCounts = { pending: number; total: number };

type TState = {
  jobId: string;
  setJobId: (value: string) => void;
  sourceVersionId: string;
  setSourceVersionId: (value: string) => void;
  sourceTranscript: string;
  setSourceTranscript: (value: string) => void;
  eventJson: string;
  setEventJson: (value: string) => void;
  prompt: string;
  setPrompt: (value: string) => void;

  chatEditMode: TChatEditMode;
  setChatEditMode: (mode: TChatEditMode) => void;
  chatInput: string;
  setChatInput: (value: string) => void;
  chatMessages: TChatMessage[];
  clearChatMessages: () => void;
  chatChunkLineCount: number;
  setChatChunkLineCount: (value: number) => void;

  sessions: TTranscriptReviewSession[];
  session: TTranscriptReviewSession | null;
  selectSessionById: (sessionId: string) => void;

  selectedLines: number[];
  toggleLine: (lineNumber: number) => void;

  previewProposalId: string | null;
  setPreviewProposalId: (value: string | null) => void;
  previewProposal: TTranscriptReviewProposal | null;
  previewChangedLines: Set<number>;
  previewProposalTranscript: (proposalId: string) => void;

  chunkRanges: TTranscriptChunkRange[];
  activeTranscriptChunk: TTranscriptChunkRange | null;
  activeTranscriptChunkIndex: number;
  selectTranscriptChunkIndex: (nextIndex: number) => void;

  showOnlyActiveChunkProposals: boolean;
  setShowOnlyActiveChunkProposals: (value: boolean | ((current: boolean) => boolean)) => void;

  focusedLineNumber: number | null;
  setFocusedLineNumber: (value: number | null) => void;

  fixtureEventCount: number;
  fixtureSource: TFixtureSource;
  loadedS3ChunkIndexes: Set<number>;
  loadedS3EventCount: number;
  emptyFixtureChunks: TTranscriptChunkRange[];

  statusMessage: string;
  loadingMessage: string;
  isBusy: boolean;

  proposals: TTranscriptReviewProposal[];
  visibleProposals: TTranscriptReviewProposal[];
  proposalStatusCounts: { accepted: number; pending: number; rejected: number; stale: number };
  proposalCountsByChunk: Map<number, TChunkProposalCounts>;
  activeChunkProposalCounts: TChunkProposalCounts;
  persistedChatSummaries: TPersistedChatSummary[];
  transcriptLines: string[];
  pendingCount: number;
  isLocalFixture: boolean;
  chatModeLineCount: number;
  canSubmitChat: boolean;

  transcriptLineRefs: MutableRefObject<Record<number, HTMLButtonElement | null>>;

  loadSessions: () => Promise<void>;
  createSession: () => Promise<void>;
  importEvents: () => Promise<void>;
  importFixtureFiles: (files: FileList | null) => Promise<void>;
  loadStyleFixFixturesFromS3: () => Promise<void>;
  decideProposal: (proposalId: string, decision: TTranscriptReviewProposalDecision) => Promise<void>;
  acceptAllPending: () => Promise<void>;
  rejectAllPending: () => Promise<void>;
  jumpToProposalLine: (proposal: TTranscriptReviewProposal, keepPreview?: boolean) => void;
  requestSelectedEdit: () => Promise<void>;
  requestWholeDocumentEdit: () => Promise<void>;
  requestChatEdit: () => Promise<void>;
  publishSession: () => Promise<void>;
};

const PageContext = createContext<TState | null>(null);

export const usePageContext = (): TState => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('usePageContext must be used within TranscriptReviewPage PageProvider');
  }
  return context;
};

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { jobId: routeJobId = '' } = useParams();
  const [searchParams] = useSearchParams();

  const [jobId, setJobId] = useState(routeJobId);
  const [sourceVersionId, setSourceVersionId] = useState('');
  const [sourceTranscript, setSourceTranscript] = useState('');
  const [eventJson, setEventJson] = useState('');
  const [prompt, setPrompt] = useState('');
  const [chatEditMode, setChatEditMode] = useState<TChatEditMode>('selection');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<TChatMessage[]>([]);
  const [chatChunkLineCount, setChatChunkLineCount] = useState(80);
  const [localSessions, setLocalSessions] = useState<TTranscriptReviewSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedLines, setSelectedLines] = useState<number[]>([]);
  const [previewProposalId, setPreviewProposalId] = useState<string | null>(null);
  const [chunkRanges, setChunkRanges] = useState<TTranscriptChunkRange[]>([]);
  const [activeTranscriptChunkIndex, setActiveTranscriptChunkIndex] = useState(0);
  const [showOnlyActiveChunkProposals, setShowOnlyActiveChunkProposals] = useState(false);
  const [focusedLineNumber, setFocusedLineNumber] = useState<number | null>(null);
  const [fixtureEventCount, setFixtureEventCount] = useState(0);
  const [fixtureSource, setFixtureSource] = useState<TFixtureSource>('none');
  const [loadedS3ChunkIndexes, setLoadedS3ChunkIndexes] = useState<Set<number>>(new Set());
  const [statusMessage, setStatusMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const hasAutoLoadedS3Fixtures = useRef(false);
  const transcriptLineRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const debouncedJobId = useDebouncedValue(jobId, JOB_ID_DEBOUNCE_MS);
  const isJobIdSettling = jobId !== debouncedJobId;
  const sessionsQueryKey = adminTranscriptReviewKeys.sessionsByJob(debouncedJobId);
  const sessionsQuery = useQuery({
    queryKey: sessionsQueryKey,
    queryFn: createFetchTranscriptReviewSessionsQueryFn(call),
    enabled: debouncedJobId !== '',
  });

  const createSessionMutation = useMutation({
    mutationFn: ({
      jobId: targetJobId,
      requestBody,
    }: {
      jobId: string;
      requestBody: CreateTranscriptReviewSessionBodyDto;
    }) =>
      call(
        () => AdminTranscriptReviewService.createSession({ jobId: targetJobId, requestBody }),
        INLINE_ERROR_CALL_OPTIONS,
      ),
  });
  const importEventsMutation = useMutation({
    mutationFn: ({ sessionId, events }: { sessionId: string; events: Record<string, unknown>[] }) =>
      call(
        () => AdminTranscriptReviewService.importEvents({ sessionId, requestBody: { events } }),
        INLINE_ERROR_CALL_OPTIONS,
      ),
  });
  const decideProposalMutation = useMutation({
    mutationFn: ({
      sessionId,
      proposalId,
      decision,
    }: {
      sessionId: string;
      proposalId: string;
      decision: TTranscriptReviewProposalDecision;
    }) =>
      call(
        () => AdminTranscriptReviewService.decideProposal({ sessionId, proposalId, requestBody: { decision } }),
        INLINE_ERROR_CALL_OPTIONS,
      ),
  });
  const requestSelectedEditMutation = useMutation({
    mutationFn: ({ sessionId, requestBody }: { sessionId: string; requestBody: RequestTranscriptReviewEditBodyDto }) =>
      call(
        () => AdminTranscriptReviewService.requestSelectedEdit({ sessionId, requestBody }),
        INLINE_ERROR_CALL_OPTIONS,
      ),
  });
  const requestWholeDocumentEditMutation = useMutation({
    mutationFn: ({ sessionId, requestBody }: { sessionId: string; requestBody: RequestTranscriptReviewEditBodyDto }) =>
      call(
        () => AdminTranscriptReviewService.requestWholeDocumentEdit({ sessionId, requestBody }),
        INLINE_ERROR_CALL_OPTIONS,
      ),
  });
  const previewEditMutation = useMutation({
    mutationFn: (requestBody: PreviewTranscriptReviewEditBodyDto) =>
      call(() => AdminTranscriptReviewService.previewEdit({ requestBody }), INLINE_ERROR_CALL_OPTIONS),
  });
  const publishSessionMutation = useMutation({
    mutationFn: (sessionId: string) =>
      call(() => AdminTranscriptReviewService.publishSession({ sessionId }), INLINE_ERROR_CALL_OPTIONS),
  });

  const fetchSessions = () =>
    queryClient.fetchQuery({
      queryKey: sessionsQueryKey,
      queryFn: createFetchTranscriptReviewSessionsQueryFn(call),
      staleTime: 0,
    });
  const fetchStyleFixManifest = () =>
    queryClient.fetchQuery({
      queryKey: adminTranscriptReviewKeys.styleFixManifest(jobId),
      queryFn: createFetchStyleFixFixtureManifestQueryFn(call),
      staleTime: FIXTURE_STALE_TIME_MS,
    });
  const fetchStyleFixChunk = (chunkIndex: number) =>
    queryClient.fetchQuery({
      queryKey: adminTranscriptReviewKeys.styleFixChunk(jobId, chunkIndex),
      queryFn: createFetchStyleFixFixtureChunkQueryFn(call),
      staleTime: FIXTURE_STALE_TIME_MS,
    });

  const serverSessions = sessionsQuery.data ?? EMPTY_SESSIONS;
  const sessions = useMemo(() => [...localSessions, ...serverSessions], [localSessions, serverSessions]);
  const session = useMemo(
    () =>
      selectedSessionId === null ? sessions[0] ?? null : sessions.find((item) => item.id === selectedSessionId) ?? null,
    [selectedSessionId, sessions],
  );

  const proposals = session?.proposals ?? [];
  const previewProposal = proposals.find((proposal) => proposal.id === previewProposalId) ?? null;
  const transcriptLines = useMemo(
    () => splitTranscriptLines(session?.draftTranscript ?? ''),
    [session?.draftTranscript],
  );
  const proposalStatusCounts = useMemo(
    () => ({
      accepted: proposals.filter((proposal) => proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.ACCEPTED).length,
      pending: proposals.filter((proposal) => proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING).length,
      rejected: proposals.filter((proposal) => proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.REJECTED).length,
      stale: proposals.filter(
        (proposal) => proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.STALE || proposal.applyErrors.length > 0,
      ).length,
    }),
    [proposals],
  );
  const pendingCount = proposalStatusCounts.pending;
  const isLocalFixture = isLocalFixtureSession(session);
  const previewChangedLines = new Set(previewProposal?.patch.changes?.map((change) => change.line) ?? []);
  const activeTranscriptChunk = chunkRanges[activeTranscriptChunkIndex] ?? null;
  const proposalCountsByChunk = useMemo(() => {
    const counts = new Map<number, TChunkProposalCounts>();

    for (const proposal of proposals) {
      const chunkIndex = getProposalChunkIndex(proposal);
      if (chunkIndex === null) {
        continue;
      }

      const count = counts.get(chunkIndex) ?? { pending: 0, total: 0 };
      count.total += 1;
      count.pending += proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING ? 1 : 0;
      counts.set(chunkIndex, count);
    }

    return counts;
  }, [proposals]);
  const activeChunkProposalCounts = activeTranscriptChunk
    ? proposalCountsByChunk.get(activeTranscriptChunk.index) ?? { pending: 0, total: 0 }
    : { pending: 0, total: 0 };
  const orderedProposals =
    activeTranscriptChunk && showOnlyActiveChunkProposals
      ? proposals.filter((proposal) => getProposalChunkIndex(proposal) === activeTranscriptChunk.index)
      : activeTranscriptChunk
        ? [
            ...proposals.filter((proposal) => getProposalChunkIndex(proposal) === activeTranscriptChunk.index),
            ...proposals.filter((proposal) => getProposalChunkIndex(proposal) !== activeTranscriptChunk.index),
          ]
        : proposals;
  const visibleProposals = orderedProposals;
  const persistedChatSummaries = useMemo(() => {
    const summaries = new Map<string, TPersistedChatSummary>();

    for (const proposal of proposals) {
      if (
        !proposal.prompt ||
        (proposal.source !== TRANSCRIPT_REVIEW_PROPOSAL_SOURCE.CHAT &&
          proposal.source !== TRANSCRIPT_REVIEW_PROPOSAL_SOURCE.LLM)
      ) {
        continue;
      }

      const mode = typeof proposal.metadata?.mode === 'string' ? proposal.metadata.mode : proposal.source;
      const key = `${proposal.targetRevision}:${mode}:${proposal.prompt}`;
      const existingSummary = summaries.get(key);
      const tokenCount = getProposalLlmTokenCount(proposal) ?? 0;
      const isFailedOrStale =
        proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.FAILED ||
        proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.STALE;
      const isPending = proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING;

      if (!existingSummary) {
        summaries.set(key, {
          createdAt: proposal.createdAt,
          failedCount: isFailedOrStale ? 1 : 0,
          key,
          mode,
          pendingCount: isPending ? 1 : 0,
          prompt: proposal.prompt,
          proposalCount: 1,
          tokenCount,
        });
        continue;
      }

      existingSummary.createdAt =
        new Date(proposal.createdAt).getTime() > new Date(existingSummary.createdAt).getTime()
          ? proposal.createdAt
          : existingSummary.createdAt;
      existingSummary.failedCount += isFailedOrStale ? 1 : 0;
      existingSummary.pendingCount += isPending ? 1 : 0;
      existingSummary.proposalCount += 1;
      existingSummary.tokenCount += tokenCount;
    }

    return Array.from(summaries.values()).sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    );
  }, [proposals]);
  const emptyFixtureChunks = chunkRanges.filter((chunk) => chunk.eventCount === 0);
  const shouldAutoLoadS3Fixtures = searchParams.get('source') === 's3';
  const loadedS3EventCount = chunkRanges
    .filter((chunk) => loadedS3ChunkIndexes.has(chunk.index))
    .reduce((count, chunk) => count + chunk.eventCount, 0);
  const chatPrompt = chatInput.trim();
  const chatModeLineCount =
    chatEditMode === 'selection'
      ? selectedLines.length
      : chatEditMode === 'chunk' && activeTranscriptChunk
        ? activeTranscriptChunk.endLine - activeTranscriptChunk.startLine + 1
        : transcriptLines.length;
  const canSubmitChat =
    !!session &&
    !!chatPrompt &&
    (chatEditMode !== 'selection' || selectedLines.length > 0) &&
    (chatEditMode !== 'chunk' || !!activeTranscriptChunk);
  const isBusy = isRunning || sessionsQuery.isLoading || isJobIdSettling;
  const visibleStatusMessage = statusMessage || (sessionsQuery.isError ? getApiErrorMessage(sessionsQuery.error) : '');

  useEffect(() => {
    setJobId(routeJobId);
  }, [routeJobId]);

  useEffect(() => {
    setLocalSessions([]);
    setSelectedSessionId(null);
    setSelectedLines([]);
    setPreviewProposalId(null);
    setChunkRanges([]);
    setActiveTranscriptChunkIndex(0);
    setShowOnlyActiveChunkProposals(false);
    setFixtureEventCount(0);
    setFixtureSource('none');
    setLoadedS3ChunkIndexes(new Set());
  }, [debouncedJobId]);

  useEffect(() => {
    if (!activeTranscriptChunk || previewProposal) {
      return;
    }

    transcriptLineRefs.current[activeTranscriptChunk.startLine]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [activeTranscriptChunk, previewProposal]);

  useEffect(() => {
    if (!previewProposal || !chunkRanges.length) {
      return;
    }

    const chunkIndex = getProposalChunkIndex(previewProposal);
    if (chunkIndex === null) {
      return;
    }

    const nextChunkIndex = chunkRanges.findIndex((chunk) => chunk.index === chunkIndex);
    if (nextChunkIndex !== -1) {
      setActiveTranscriptChunkIndex(nextChunkIndex);
    }
  }, [chunkRanges, previewProposal]);

  const setActiveSession = async (nextSession: TTranscriptReviewSession) => {
    if (isLocalFixtureSession(nextSession)) {
      setLocalSessions((prev) => upsertSession(prev, nextSession));
    } else {
      await queryClient.cancelQueries({ queryKey: sessionsQueryKey });
      queryClient.setQueryData<TTranscriptReviewSession[]>(sessionsQueryKey, (prev) =>
        upsertSession(prev ?? [], nextSession),
      );
    }
    setSelectedSessionId(nextSession.id);
  };

  const resetFixtureView = () => {
    setSelectedLines([]);
    setPreviewProposalId(null);
    setChunkRanges([]);
    setActiveTranscriptChunkIndex(0);
    setShowOnlyActiveChunkProposals(false);
    setFixtureEventCount(0);
    setFixtureSource('none');
    setLoadedS3ChunkIndexes(new Set());
  };

  const run = async (action: () => Promise<void>, nextLoadingMessage = '') => {
    setIsRunning(true);
    setLoadingMessage(nextLoadingMessage);
    setStatusMessage('');
    try {
      await action();
    } catch (error) {
      setStatusMessage(getApiErrorMessage(error));
    } finally {
      setIsRunning(false);
      setLoadingMessage('');
    }
  };

  const loadSessions = () =>
    run(async () => {
      await fetchSessions();
      setLocalSessions([]);
      setSelectedSessionId(null);
      resetFixtureView();
    });

  const createSession = () =>
    run(async () => {
      let nextSession: TTranscriptReviewSession;
      try {
        nextSession = normalizeSession(
          await createSessionMutation.mutateAsync({
            jobId,
            requestBody: {
              sourceVersionId: sourceVersionId || undefined,
              transcript: sourceTranscript || undefined,
            },
          }),
        );
      } catch (error) {
        if (!isNotFoundApiError(error) || !sourceTranscript.trim()) {
          throw error;
        }
        nextSession = createLocalFixtureSession(jobId, sourceTranscript);
        setStatusMessage('Loaded as a temporary local fixture because this job is not in the local database.');
      }

      await setActiveSession(nextSession);
      resetFixtureView();
    });

  const importEvents = () =>
    run(async () => {
      const events = parseEvents(eventJson);

      if (!session) {
        const transcript = sourceTranscript.trim() || getFirstEventTranscriptBefore(events);
        if (!transcript) {
          throw new Error('Paste source transcript or event JSON with transcriptBefore first.');
        }

        const localSession = createLocalFixtureSession(jobId || 'local-fixture', transcript);
        const nextSession = {
          ...localSession,
          proposals: buildLocalFixtureProposals(localSession, events).sort(
            (left, right) => left.sequence - right.sequence,
          ),
          updatedAt: new Date().toISOString(),
        };
        await setActiveSession(nextSession);
        setSelectedLines([]);
        setPreviewProposalId(nextSession.proposals[0]?.id ?? null);
        setFixtureEventCount(events.length);
        setFixtureSource('pasted-events');
        setLoadedS3ChunkIndexes(new Set());
        setStatusMessage('Loaded event JSON as a temporary local fixture.');
        return;
      }

      if (isLocalFixtureSession(session)) {
        const nextSession = {
          ...session,
          proposals: [...session.proposals, ...buildLocalFixtureProposals(session, events)].sort(
            (left, right) => left.sequence - right.sequence,
          ),
          updatedAt: new Date().toISOString(),
        };
        await setActiveSession(nextSession);
        setPreviewProposalId(nextSession.proposals[0]?.id ?? null);
        setFixtureEventCount(events.length);
        setFixtureSource('pasted-events');
        setLoadedS3ChunkIndexes(new Set());
        return;
      }

      await setActiveSession(
        normalizeSession(await importEventsMutation.mutateAsync({ sessionId: session.id, events })),
      );
    });

  const importFixtureFiles = (files: FileList | null) =>
    run(async () => {
      const fixture = await readFixtureFiles(files);
      if (!fixture.events.length) {
        throw new Error('No fixture JSON files were selected.');
      }

      setEventJson(serializeEvents(fixture.events));

      if (session && !isLocalFixtureSession(session)) {
        const nextSession = normalizeSession(
          await importEventsMutation.mutateAsync({ sessionId: session.id, events: fixture.events }),
        );
        await setActiveSession(nextSession);
        setPreviewProposalId(fixture.chunks.length ? null : nextSession.proposals[0]?.id ?? null);
        setChunkRanges(fixture.chunks);
        setActiveTranscriptChunkIndex(0);
        setShowOnlyActiveChunkProposals(fixture.chunks.length > 0);
        setFixtureEventCount(fixture.events.length);
        setFixtureSource('local-folder');
        setLoadedS3ChunkIndexes(new Set());
        setStatusMessage(`Loaded ${fixture.events.length} fixture event files.`);
        return;
      }

      const transcript =
        fixture.transcript ||
        sourceTranscript.trim() ||
        session?.sourceTranscript ||
        getFirstEventTranscriptBefore(fixture.events);
      if (!session && !transcript) {
        throw new Error('Selected fixture files do not include transcriptBefore.');
      }

      const targetSession =
        session ??
        createLocalFixtureSession(
          jobId || (typeof fixture.events[0]?.jobId === 'string' ? fixture.events[0].jobId : 'local-fixture'),
          transcript,
        );

      const nextSession = {
        ...targetSession,
        sourceTranscript: targetSession.sourceTranscript || transcript,
        draftTranscript: targetSession.draftTranscript || transcript,
        proposals: [
          ...targetSession.proposals,
          ...buildLocalFixtureProposals(targetSession, fixture.events, fixture.chunks),
        ].sort((left, right) => left.sequence - right.sequence),
        updatedAt: new Date().toISOString(),
      };

      await setActiveSession(nextSession);
      setSelectedLines([]);
      setPreviewProposalId(fixture.chunks.length ? null : nextSession.proposals[0]?.id ?? null);
      setChunkRanges(fixture.chunks);
      setActiveTranscriptChunkIndex(0);
      setShowOnlyActiveChunkProposals(fixture.chunks.length > 0);
      setFixtureEventCount(fixture.events.length);
      setFixtureSource('local-folder');
      setLoadedS3ChunkIndexes(new Set());
      setStatusMessage(`Loaded ${fixture.events.length} fixture event files.`);
    });

  const loadS3FixtureChunkEvents = (chunkIndex: number) =>
    run(async () => {
      if (!session || loadedS3ChunkIndexes.has(chunkIndex)) {
        return;
      }

      const fixtureChunk = await fetchStyleFixChunk(chunkIndex);
      if (isLocalFixtureSession(session)) {
        await setActiveSession(appendLocalFixtureEvents(session, fixtureChunk.events, chunkRanges));
      } else {
        await setActiveSession(
          normalizeSession(
            await importEventsMutation.mutateAsync({
              sessionId: session.id,
              events: fixtureChunk.events.map((event) => offsetStyleFixEventForTranscript(event, chunkRanges)),
            }),
          ),
        );
      }
      setLoadedS3ChunkIndexes((currentIndexes) => new Set([...currentIndexes, chunkIndex]));
      setEventJson(serializeEvents(fixtureChunk.events));
      setStatusMessage(
        `${isLocalFixtureSession(session) ? 'Loaded' : 'Persisted'} ${fixtureChunk.events.length} S3 fixture events for chunk ${chunkIndex}.`,
      );
    }, `Loading events for chunk ${chunkIndex}...`);

  const loadStyleFixFixturesFromS3 = () =>
    run(async () => {
      const manifest = await fetchStyleFixManifest();
      if (!manifest.chunks.length || !manifest.transcript) {
        throw new Error('No stylefix replay artifacts were found for this job.');
      }

      const firstChunk = manifest.chunks[0];
      setLoadingMessage(`Loading events for chunk ${firstChunk.index}...`);
      const fixtureChunk = await fetchStyleFixChunk(firstChunk.index);
      const existingSessions = await fetchSessions();
      const existingS3Session = existingSessions.find(
        (item) =>
          item.status === TRANSCRIPT_REVIEW_SESSION_STATUS.DRAFT &&
          item.title === s3FixtureReviewTitle &&
          item.sourceTranscript === manifest.transcript,
      );
      let nextSession: TTranscriptReviewSession;
      let nextLoadedChunkIndexes = existingS3Session
        ? getLoadedS3ChunkIndexesFromSession(existingS3Session)
        : new Set<number>();
      let persisted = !!existingS3Session;

      if (existingS3Session) {
        nextSession = existingS3Session;
      } else {
        try {
          nextSession = normalizeSession(
            await createSessionMutation.mutateAsync({
              jobId,
              requestBody: { transcript: manifest.transcript, title: s3FixtureReviewTitle },
            }),
          );
          persisted = true;
        } catch (error) {
          if (!isNotFoundApiError(error)) {
            throw error;
          }
          const localSession = createLocalFixtureSession(jobId, manifest.transcript);
          nextSession = appendLocalFixtureEvents(localSession, fixtureChunk.events, manifest.chunks);
          nextLoadedChunkIndexes = new Set([firstChunk.index]);
          persisted = false;
        }
      }

      if (persisted && !nextLoadedChunkIndexes.has(firstChunk.index)) {
        nextSession = normalizeSession(
          await importEventsMutation.mutateAsync({
            sessionId: nextSession.id,
            events: fixtureChunk.events.map((event) => offsetStyleFixEventForTranscript(event, manifest.chunks)),
          }),
        );
        nextLoadedChunkIndexes = getLoadedS3ChunkIndexesFromSession(nextSession);
      }

      setSourceTranscript(manifest.transcript);
      setEventJson(serializeEvents(fixtureChunk.events));
      await setActiveSession(nextSession);
      setSelectedLines([]);
      setPreviewProposalId(null);
      setChunkRanges(manifest.chunks);
      setActiveTranscriptChunkIndex(0);
      setShowOnlyActiveChunkProposals(true);
      setFixtureEventCount(manifest.eventCount);
      setFixtureSource('s3');
      setLoadedS3ChunkIndexes(nextLoadedChunkIndexes);
      setStatusMessage(
        `${persisted ? 'Persisted' : 'Loaded local fallback for'} S3 fixture manifest and ${fixtureChunk.events.length} events for chunk ${firstChunk.index}.`,
      );
    }, 'Loading S3 fixture manifest...');

  useEffect(() => {
    if (!shouldAutoLoadS3Fixtures || !jobId || hasAutoLoadedS3Fixtures.current) {
      return;
    }

    hasAutoLoadedS3Fixtures.current = true;
    loadStyleFixFixturesFromS3();
  }, [jobId, shouldAutoLoadS3Fixtures]);

  const applyDecidedSession = async (nextSession: TTranscriptReviewSession, proposalId: string) => {
    await setActiveSession(nextSession);
    setSelectedLines([]);
    const decidedProposal = nextSession.proposals.find((item) => item.id === proposalId);
    setPreviewProposalId(
      decidedProposal?.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.STALE || decidedProposal?.applyErrors.length
        ? proposalId
        : getNextPendingProposalId(nextSession.proposals, proposalId),
    );
  };

  const decideProposal = (proposalId: string, decision: TTranscriptReviewProposalDecision) =>
    run(async () => {
      if (!session) {
        return;
      }

      if (isLocalFixtureSession(session)) {
        const proposal = session.proposals.find((item) => item.id === proposalId);
        if (!proposal || proposal.status !== TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING) {
          return;
        }

        await applyDecidedSession(
          decision === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.REJECTED
            ? rejectLocalFixtureProposal(session, proposalId)
            : acceptLocalFixtureProposal(session, proposalId),
          proposalId,
        );
        return;
      }

      await applyDecidedSession(
        normalizeSession(await decideProposalMutation.mutateAsync({ sessionId: session.id, proposalId, decision })),
        proposalId,
      );
    });

  const decideAllPending = (decision: TTranscriptReviewProposalDecision) =>
    run(async () => {
      if (!session) {
        return;
      }

      const pendingProposals = session.proposals.filter(
        (proposal) => proposal.status === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.PENDING,
      );

      if (isLocalFixtureSession(session)) {
        const decideLocally =
          decision === TRANSCRIPT_REVIEW_PROPOSAL_STATUS.REJECTED
            ? rejectLocalFixtureProposal
            : acceptLocalFixtureProposal;
        const nextSession = pendingProposals.reduce(
          (currentSession, proposal) => decideLocally(currentSession, proposal.id),
          session,
        );
        await setActiveSession(nextSession);
        setPreviewProposalId(null);
        return;
      }

      let nextSession = session;
      for (const proposal of pendingProposals) {
        nextSession = normalizeSession(
          await decideProposalMutation.mutateAsync({ sessionId: nextSession.id, proposalId: proposal.id, decision }),
        );
      }
      await setActiveSession(nextSession);
      setPreviewProposalId(null);
    });

  const acceptAllPending = () => decideAllPending(TRANSCRIPT_REVIEW_PROPOSAL_STATUS.ACCEPTED);

  const rejectAllPending = () => decideAllPending(TRANSCRIPT_REVIEW_PROPOSAL_STATUS.REJECTED);

  const previewProposalTranscript = (proposalId: string) => {
    setPreviewProposalId((currentProposalId) => (currentProposalId === proposalId ? null : proposalId));
  };

  const selectTranscriptChunkIndex = (nextIndex: number) => {
    if (!chunkRanges.length) {
      return;
    }

    const boundedIndex = Math.min(Math.max(nextIndex, 0), chunkRanges.length - 1);
    const nextChunk = chunkRanges[boundedIndex];
    setPreviewProposalId(null);
    setActiveTranscriptChunkIndex(boundedIndex);

    if (fixtureSource === 's3' && nextChunk && !loadedS3ChunkIndexes.has(nextChunk.index)) {
      loadS3FixtureChunkEvents(nextChunk.index);
    }
  };

  const jumpToProposalLine = (proposal: TTranscriptReviewProposal, keepPreview = false) => {
    const line = proposal.patch.changes?.[0]?.line;
    const lineNumber = typeof line === 'number' && Number.isInteger(line) ? line : null;
    if (lineNumber === null) {
      return;
    }

    const chunkIndex = getProposalChunkIndex(proposal);
    if (chunkIndex !== null) {
      const nextChunkIndex = chunkRanges.findIndex((chunk) => chunk.index === chunkIndex);
      if (nextChunkIndex !== -1) {
        setActiveTranscriptChunkIndex(nextChunkIndex);
      }
    }

    setFocusedLineNumber(lineNumber);
    if (!keepPreview) {
      setPreviewProposalId(null);
    }
    window.setTimeout(() => {
      transcriptLineRefs.current[lineNumber]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  const requestSelectedEdit = () =>
    run(async () => {
      if (!session) {
        return;
      }

      if (isLocalFixtureSession(session)) {
        throw new Error('LLM edits require a database-backed review session.');
      }

      await setActiveSession(
        normalizeSession(
          await requestSelectedEditMutation.mutateAsync({
            sessionId: session.id,
            requestBody: {
              prompt,
              lineNumbers: selectedLines,
              targetRevision: session.currentRevision,
              editMode: 'selection',
            },
          }),
        ),
      );
    });

  const requestWholeDocumentEdit = () =>
    run(async () => {
      if (!session) {
        return;
      }

      if (isLocalFixtureSession(session)) {
        throw new Error('LLM edits require a database-backed review session.');
      }

      await setActiveSession(
        normalizeSession(
          await requestWholeDocumentEditMutation.mutateAsync({
            sessionId: session.id,
            requestBody: {
              prompt,
              targetRevision: session.currentRevision,
            },
          }),
        ),
      );
    });

  const requestChatEdit = () =>
    run(async () => {
      if (!session || !chatPrompt) {
        return;
      }

      const startedProposalCount = session.proposals.length;
      const messageId = `chat-${Date.now()}`;
      const mode = chatEditMode;
      const promptText = chatPrompt;

      setChatMessages((currentMessages) => [
        {
          createdAt: new Date().toISOString(),
          id: messageId,
          mode,
          prompt: promptText,
          proposalCount: 0,
          status: 'running',
          summary: `Generating proposals for ${chatModeLineCount} lines.`,
        },
        ...currentMessages,
      ]);

      try {
        let nextSession: TTranscriptReviewSession;
        if (isLocalFixtureSession(session)) {
          const previewProposals = getResponsePayload<TTranscriptReviewProposal[]>(
            await previewEditMutation.mutateAsync({
              transcript: session.draftTranscript,
              prompt: promptText,
              chunkLineCount: chatChunkLineCount,
              editMode: mode,
              endLine: mode === 'chunk' ? activeTranscriptChunk?.endLine : undefined,
              lineNumbers: mode === 'selection' ? selectedLines : undefined,
              startLine: mode === 'chunk' ? activeTranscriptChunk?.startLine : undefined,
              targetRevision: session.currentRevision,
            }),
          );
          nextSession = appendLocalPreviewProposals(session, previewProposals);
        } else if (mode === 'selection') {
          nextSession = normalizeSession(
            await requestSelectedEditMutation.mutateAsync({
              sessionId: session.id,
              requestBody: {
                prompt: promptText,
                lineNumbers: selectedLines,
                targetRevision: session.currentRevision,
                editMode: 'selection',
              },
            }),
          );
        } else if (mode === 'chunk' && activeTranscriptChunk) {
          nextSession = normalizeSession(
            await requestSelectedEditMutation.mutateAsync({
              sessionId: session.id,
              requestBody: {
                prompt: promptText,
                startLine: activeTranscriptChunk.startLine,
                endLine: activeTranscriptChunk.endLine,
                targetRevision: session.currentRevision,
                editMode: 'chunk',
              },
            }),
          );
        } else {
          nextSession = normalizeSession(
            await requestWholeDocumentEditMutation.mutateAsync({
              sessionId: session.id,
              requestBody: {
                prompt: promptText,
                targetRevision: session.currentRevision,
                chunkLineCount: chatChunkLineCount,
              },
            }),
          );
        }

        const newProposals = nextSession.proposals.slice(startedProposalCount);
        const proposalCount = newProposals.length;
        const firstChangedProposal = newProposals.find((proposal) => proposal.patch.changes?.length);
        await setActiveSession(nextSession);
        setChatInput('');
        setPreviewProposalId(firstChangedProposal?.id ?? null);
        if (firstChangedProposal) {
          jumpToProposalLine(firstChangedProposal, true);
        }
        setChatMessages((currentMessages) =>
          currentMessages.map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  proposalCount,
                  status: 'completed',
                  summary: `${proposalCount} proposals added.`,
                }
              : message,
          ),
        );
      } catch (error) {
        setChatMessages((currentMessages) =>
          currentMessages.map((message) =>
            message.id === messageId
              ? {
                  ...message,
                  status: 'failed',
                  summary: getApiErrorMessage(error),
                }
              : message,
          ),
        );
        throw error;
      }
    }, 'Generating transcript review proposals...');

  const publishSession = () =>
    run(async () => {
      if (!session) {
        return;
      }

      if (isLocalFixtureSession(session)) {
        throw new Error('Publishing requires a database-backed review session.');
      }

      const body = await publishSessionMutation.mutateAsync(session.id);
      await setActiveSession(normalizeSession(body.session));
      setStatusMessage(`Published version ${body.versionId}`);
    });

  const toggleLine = (lineNumber: number) => {
    setSelectedLines((prev) =>
      prev.includes(lineNumber)
        ? prev.filter((line) => line !== lineNumber)
        : [...prev, lineNumber].sort((a, b) => a - b),
    );
  };

  const selectSessionById = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setSelectedLines([]);
  };

  const clearChatMessages = () => setChatMessages([]);

  const value: TState = {
    jobId,
    setJobId,
    sourceVersionId,
    setSourceVersionId,
    sourceTranscript,
    setSourceTranscript,
    eventJson,
    setEventJson,
    prompt,
    setPrompt,
    chatEditMode,
    setChatEditMode,
    chatInput,
    setChatInput,
    chatMessages,
    clearChatMessages,
    chatChunkLineCount,
    setChatChunkLineCount,
    sessions,
    session,
    selectSessionById,
    selectedLines,
    toggleLine,
    previewProposalId,
    setPreviewProposalId,
    previewProposal,
    previewChangedLines,
    previewProposalTranscript,
    chunkRanges,
    activeTranscriptChunk,
    activeTranscriptChunkIndex,
    selectTranscriptChunkIndex,
    showOnlyActiveChunkProposals,
    setShowOnlyActiveChunkProposals,
    focusedLineNumber,
    setFocusedLineNumber,
    fixtureEventCount,
    fixtureSource,
    loadedS3ChunkIndexes,
    loadedS3EventCount,
    emptyFixtureChunks,
    statusMessage: visibleStatusMessage,
    loadingMessage,
    isBusy,
    proposals,
    visibleProposals,
    proposalStatusCounts,
    proposalCountsByChunk,
    activeChunkProposalCounts,
    persistedChatSummaries,
    transcriptLines,
    pendingCount,
    isLocalFixture,
    chatModeLineCount,
    canSubmitChat,
    transcriptLineRefs,
    loadSessions,
    createSession,
    importEvents,
    importFixtureFiles,
    loadStyleFixFixturesFromS3,
    decideProposal,
    acceptAllPending,
    rejectAllPending,
    jumpToProposalLine,
    requestSelectedEdit,
    requestWholeDocumentEdit,
    requestChatEdit,
    publishSession,
  };

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
};
